/**
 * warmEndpoints — start the Control Center's read functions before the client
 * clicks anything.
 *
 * 🔴 WHY (MEASURED 2026-09-22, John's CC review items #6, #7, #32, #36b).
 *
 * John reported Setup taking "over 10 seconds", the "See yours" document previews
 * ~10s each, "What I know about your trade" slow, and My Tickets slow. Four
 * separate surfaces.
 *
 * His suggested remedy was to PRELOAD THE CONTENT. Profiling first says the content
 * was never the problem — `previewsDataHttp` executions measured 476 / 698 / 771 /
 * 1050 ms in production, all sub-second. Preloading data would have moved several
 * seconds of work to login and made the whole Control Center slow to open, while
 * fixing nothing.
 *
 * The cost is COLD START, and our own metric is blind to it: Cloud Functions logs
 * "Function execution took N ms" for the HANDLER only, so container startup never
 * appears in it. Everything we record looked healthy.
 *
 * Proven with a cold-vs-warm probe against the live endpoint, on a request that does
 * NO work at all (no auth, HTTP 400):
 *
 *     probe 1 (cold)  ttfb = 6.628 s
 *     probe 2 (warm)  ttfb = 0.135 s
 *     probe 3 (warm)  ttfb = 0.121 s
 *
 * ~50x, and that 6.6s is pure container startup before any handler runs. Add the
 * handler and the round trip and you have the ten seconds he saw. These are 1st-gen
 * Node 20 functions at 512MB with `minInstances` unset, each loading an 890KB index
 * across an 88MB tree — a textbook cold-start profile.
 *
 * ⚠️ WHY NOT `minInstances: 1`, which this repo already uses. It works and it is the
 * measured pattern (checkout, bookingPageHttp, clientConsentPageHttp), but it costs
 * ~$2.74-2.98/mo per function, quoted at full price because the free tier is shared
 * across 350+ deployed functions. Warming four more is ~$12/mo of standing cost for a
 * problem that only bites on the FIRST click after an idle period. This is free, so it
 * goes first; if it proves insufficient, minInstances is the escalation and John
 * approves each one (the existing ones are booked in Financial.tsx FIXED_COSTS).
 *
 * ⚠️ THE REQUESTS CARRY NO clientId ON PURPOSE. Each returns 400 immediately without
 * touching data, so this cannot mutate anything, cannot leak across clients, and
 * cannot be slow itself. Starting the CONTAINER is the entire objective — the handler
 * was never the cost.
 */

/** The read endpoints behind the four surfaces John measured as slow. */
const WARM_PATHS = [
  '/api/setup/roadmap',              // Setup → "Where we are"
  '/api/setup/previews',             // Setup → "What Ava made"
  '/api/business-profile/industry-pack', // Ava's Knowledge → "What I know about your trade"
  '/api/support/my-tickets',         // Settings → My Tickets
  // 2026-10-14 (John: warm on login instead of paying minInstances). Measured that day
  // from 14 days of logs: warm ~150 ms, cold 7–13 s, cold on roughly 1 in 3 to most
  // requests. Probed unauthenticated: every one refuses at once (400/405) — the
  // 5–7 s answer time was the container starting, not the handler.
  '/api/visible-sequences',          // Settings → On a schedule
  '/api/daily-texts/options',        // Settings → morning / evening texts
  '/api/business-profile/price-list', // Price list
  '/api/connected-accounts/list',    // Settings → Integrations
  '/api/phone-greetings',            // Settings → Phone
  '/api/consent-coverage',           // consent card
  '/api/document-chase',             // Documents → chase
  '/api/open-questions',             // Dashboard / Setup → what I still need
  '/api/referral-progress',          // Refer & Earn
  '/api/prospecting',                // Prospecting
] as const

let warmed = false

/**
 * Fire-and-forget. Never awaited, never throws, never blocks a render.
 * Idempotent per page load — a second call is a no-op.
 */
export function warmEndpoints(): void {
  if (warmed) return
  warmed = true
  if (typeof fetch !== 'function') return
  for (const path of WARM_PATHS) {
    try {
      // `keepalive` so a fast navigation does not cancel the warm-up.
      void fetch(path, { method: 'GET', keepalive: true }).catch(() => {})
    } catch {
      /* warming is best-effort by definition — never surface a failure */
    }
  }
}

/** Exported for the test: the paths must stay in step with what the pages call. */
export const WARMED_PATHS: readonly string[] = WARM_PATHS
